import { combineLatest, debounceTime, delay, filter, fromEvent, map, startWith, Subscription, take, tap } from "rxjs";
import { activeInsert$, addInsert$, boardMode$, complete$, deleteInsert$, depth$, height$, inserts$, message$, mobileToggle$, modal$, snapObject$, trayColor$, trayData$, updateInsert$, width$ } from "../lib/state";
import StorageService from "../lib/storage.service";
import Logger from '../../js/lib/logger';
import { InsertConfigData, InsertData, MessageDetail, ModeType, SnapObject, TrayData } from "../lib/types";
import SvgInsertsContainer from "./SvgInsertsContainer";
import Insert from "./Insert";
import { generateInsertId, getNearestInsertDataDown, getNearestInsertDataLeft, getNearestInsertDataRight, getNearestInsertDataUp, getPercentAreaCovered, updateInsertDataToFillGaps } from "../lib/util.functions";
import { Measurement, UNIT_IN_PIXELS } from "../lib/constants";
import IndexDBService from "../lib/indexDB.service";

const indexDBService = new IndexDBService('jt-db');

class TrayBuilderMain extends HTMLElement {
  #NAME = 'TrayBuilderMain';
  #subs: Subscription[] = [];
  #messageBars: NodeListOf<HTMLElement>;
  #helpBtns: NodeListOf<HTMLButtonElement>;
  #transformBtns: NodeListOf<HTMLButtonElement>;
  #insertsContainer: SvgInsertsContainer;
  #activeInsert: Insert | null = null;
  #svgStyles?: string;

  constructor() {
    super();
    Logger.info(`${this.#NAME} > constructor`);
    this.#messageBars = this.querySelectorAll('message-bar');
    this.#helpBtns = this.querySelectorAll('.custom-tray-builder__help-btn');
    this.#transformBtns = this.querySelectorAll('.custom-tray-builder__transform-btn');
    this.#insertsContainer = this.querySelector('svg-inserts-container') as SvgInsertsContainer;
  }

  connectedCallback(): void {
    Logger.info(`${this.#NAME} > connectedCallback`);
    //
    this.#subs = [
      message$.subscribe((val) => this.#onMessage(val)),
      width$.subscribe((val) => StorageService.save('width', val, true)),
      depth$.subscribe((val) => StorageService.save('depth', val, true)),
      height$.subscribe((val) => StorageService.save('height', val, true)),
      inserts$.subscribe((val) => {
        StorageService.save('inserts', val, true);
        this.#transformBtns.forEach(btn => {
          if (btn.dataset.type === 'fillgaps') btn.disabled = !val.length;
        });
      }),
      mobileToggle$.subscribe((val) => StorageService.save('mobileToggle', val, true)),
      addInsert$.subscribe((val) => this.#onAddInsert(val)),
      deleteInsert$.subscribe((id) => this.#onDeleteInsert(id)),
      updateInsert$.subscribe((val) => this.#onUpdateInsert(val)),
      activeInsert$.subscribe(insert => this.#onActiveInsert(insert)),
      combineLatest({data: trayData$, mode: boardMode$, snapObject: snapObject$})
        .pipe(debounceTime(100))
        .subscribe((obj) => this.#checkComplete(obj)),
      //
      fromEvent<MouseEvent>(this, 'click')
        .pipe(
          map(e => (e.target as HTMLElement).className),
          filter(className => className !== 'custom-tray-builder__transform-btn' && className !== 'theme-btn-wrap'))
        .subscribe(() => this.#onClick()),
      //
      fromEvent(window, 'hashchange').pipe(
          map(() => location.hash),
          startWith(location.hash),
          tap((val) => { if (val === '#start') this.#reset() }),
          filter((val) => val === '#help' || val === '#start'),
          delay(20)
        )
        .subscribe(() => modal$.next({ contentType: 'help', show: true })),
      //
      fromEvent(this.#helpBtns, 'click').subscribe(() => modal$.next({ contentType: 'help', show: true })),
      // 
      fromEvent(this.#transformBtns, 'click')
        .pipe(map((e) => (e.currentTarget as HTMLButtonElement).dataset.type))
        .subscribe((type) => this.#onTransformBtn(type)),
    ];
  }

  disconnectedCallback(): void {
    Logger.info(`${this.#NAME} > disconnectedCallback`);
    this.#subs.forEach(sub => sub.unsubscribe());
  }

  async #checkComplete(obj: { data: TrayData, mode: ModeType, snapObject: SnapObject}) {
    if (obj.data.inserts.length && obj.snapObject.x.length && obj.mode === 'nuetral') {
      Logger.info(`${this.#NAME} > #checkComplete`);
      const per = getPercentAreaCovered(obj.data.inserts);
      const complete = per == 1;
      /* const point = this.#insertsContainer.getNextAvailablePoint(Measurement.UNIT_FRACTION_PX, Measurement.UNIT_FRACTION_PX); */
      /* console.log('point', point, !point); */
      if (complete) {
        if (!indexDBService.db) await indexDBService.init();
        const svg = this.#insertsContainer.svg as SVGElement;
        const border = (document.querySelector('rect.svg-grid__border') as SVGRectElement).cloneNode();
        svg.prepend(border);
        if (!this.#svgStyles) this.#setSVGStyles(svg);
        const styleEl = document.createElement('style');
        svg.prepend(styleEl);
        if (this.#svgStyles) styleEl.outerHTML = this.#svgStyles;
        const html = svg.outerHTML;
        svg.querySelector('style')?.remove();
        svg.querySelector('rect.svg-grid__border')?.remove();
        const src = 'data:image/svg+xml;base64,' + window.btoa(unescape(encodeURIComponent(html)));
        const resp = await fetch(src);
        const blob = await resp.blob();
        const file = new File([blob], 'custom-tray-layout.svg', { type: blob.type });
        const request = await indexDBService.addFile(file,true);
        if (request.error) {
          throw new Error('There was a problem saving the tray layout image.');
        } else {
          Logger.info(`${this.#NAME} > #addFile > custom-tray-layout.svg`);
        }
      } else if (this.#activeInsert) {
        const { data, hasChanges } = updateInsertDataToFillGaps(
          this.#activeInsert.data, 
          { right: true, left: true, top: true, bottom: true }
        );
        if (hasChanges) updateInsert$.next(data);
      }
      complete$.next(complete);
    }
  }

  #setSVGStyles(svg: SVGElement) {
    const rect = svg.querySelector('.inner-rect') as SVGRectElement;
    const text1 = svg.querySelector('.text-1') as SVGTextElement;
    const text2 = svg.querySelector('.text-2') as SVGTextElement;
    const rectStyle = window.getComputedStyle(rect);
    const text1Style = window.getComputedStyle(text1);
    const text2Style = window.getComputedStyle(text2);
    this.#svgStyles = `
      <style>
        rect.inner-rect, rect.svg-grid__border {
          fill: ${rectStyle.fill};
          stroke: ${rectStyle.stroke};
          stroke-width: ${rectStyle.strokeWidth};
        }
        rect.outer-rect {
          display: none;
        }
        text {
          fill: ${text1Style.fill};
          font-fanily: ${text1Style.fontFamily};
        }
        .text1 {
          font-size: ${text1Style.fontSize};
          font-weight: ${text1Style.fontWeight};
          line-height: ${text1Style.lineHeight};
          letter-spacing: ${text1Style.letterSpacing};
        }
        .text2 {
          font-size: ${text2Style.fontSize};
          font-weight: ${text2Style.fontWeight};
          line-height: ${text2Style.lineHeight};
          letter-spacing: ${text2Style.letterSpacing};
        }
        .rect-transformer,
        .info-group use,
        .delete-btn {
          display: none;
        }
      </style>
    `;
  }

  #onMessage(val: MessageDetail) {
    Logger.info(`${this.#NAME} > #onMessage >`, val);
    this.#messageBars?.forEach(el => { 
      if (val.type) {
        el.dataset.type = val.type;
        if (val.type === 'empty spaces') {
          boardMode$.next('error');
        }
      }
      el.dataset.show = val.show.toString();
      if (val.show) {
        window.scrollTo({ top: this.clientTop, behavior: 'smooth'});
      } else {
        boardMode$.next('nuetral');
      }
    })
  }

  #onAddInsert(val: InsertConfigData) {
    Logger.info(`${this.#NAME} > #onAddInsert >`, val);
    const { width, height } = Insert.getWidthHeight(val.type, val.unitWidth, val.unitHeight);
    const point = this.#insertsContainer.getNextAvailablePoint(width, height);
    if (point) {
      inserts$.next([...inserts$.value, {...val, ...point, width, height }]);
    } else {
      setTimeout(() => {
        message$.next({ type: 'add', show: true });
      }, 0);
    }
  }

  #onDeleteInsert(id: string) {
    Logger.info(`${this.#NAME} > #onDeleteInsert > id:${id}`);
    boardMode$.next('nuetral');
    inserts$.next(inserts$.value.filter(dta => dta.id !== id));
  }

  #onUpdateInsert(val: InsertData) {
    Logger.info(`${this.#NAME} > #onUpdateInsert >`, val);
    const arr = inserts$.value.map(insert => {
      if (insert.id === val.id) {
        return { ...insert, ...val};
      } else {
        return insert
      }
    });
    inserts$.next(arr);
  }

  #onActiveInsert(insert: Insert | null) {
    Logger.info(`${this.#NAME} > #onActiveInsert > ${insert ? insert.id : 'null'}`);
    this.#activeInsert = insert;
    this.#transformBtns.forEach(btn => {
      if (btn.dataset.type !== 'fillgaps') btn.disabled = !insert;
    });
  }

  #onTransformBtn(type: string | undefined) {
    Logger.info(`${this.#NAME} > #onTransformBtn > ${type}`);
    this.#hideMessage();
    switch (type) {
      case 'rotate':
        if (this.#activeInsert) {
          const { width, height, id, x, y } = this.#activeInsert.data;
          const point = this.#insertsContainer.getNextAvailablePoint(height, width, id, { x, y });
          if (point) {
            this.#activeInsert.data = { width: height, height: width, x: point.x, y: point.y };
            updateInsert$.next(this.#activeInsert.data);
          } else {
            message$.next({ type: 'rotate', show: true });
          }
        }
        
        break;

      case 'duplicate':
        if (this.#activeInsert) {
          const { width, height } = this.#activeInsert.data;
          const point = this.#insertsContainer.getNextAvailablePoint(width, height);
          if (point) {
            const insertData: InsertData = {
              ...this.#activeInsert.data,
              ...point,
              id: generateInsertId()
            };
            inserts$.next([...inserts$.value, insertData]);
            updateInsert$.next(this.#activeInsert.data);
            if (this.#insertsContainer.transformer) this.#insertsContainer.svg?.append(this.#activeInsert.element, this.#insertsContainer.transformer.element);
          } else {
            message$.next({ type: 'duplicate', show: true });
          }
        }
        break;

      case 'delete':
        if (this.#activeInsert) this.#activeInsert.remove();
        break;

      case 'expand-l':
        if (this.#activeInsert) {
          if (this.#activeInsert.type === 'cushion' || this.#activeInsert.type === 'pillow') {
            message$.next({ type: this.#activeInsert.type, show: true });
            return;
          }
          const widthFrom0 = this.#activeInsert.data.x + this.#activeInsert.data.width;
          const closestInsertData = getNearestInsertDataLeft(this.#activeInsert.data);
          const newWidth = closestInsertData ? widthFrom0 - (closestInsertData.x + closestInsertData.width) : widthFrom0;
          const updatedData: InsertData = {
            ...this.#activeInsert.data, 
            width: newWidth,
            unitWidth: newWidth * UNIT_IN_PIXELS,
            x: closestInsertData ? closestInsertData.x + closestInsertData.width : 0
          };
          this.#activeInsert.data = updatedData;
          updateInsert$.next(updatedData);
        }
        break;

      case 'expand-r':
        if (this.#activeInsert) {
          if (this.#activeInsert.type === 'cushion' || this.#activeInsert.type === 'pillow') {
            message$.next({ type: this.#activeInsert.type, show: true });
            return;
          }
          const gridWidth = (width$.value * UNIT_IN_PIXELS) - Measurement.SEP_WIDTH_PX;
          const closestInsertData = getNearestInsertDataRight(this.#activeInsert.data, gridWidth);
          const newWidth = closestInsertData ? closestInsertData.x - this.#activeInsert.data.x : gridWidth - this.#activeInsert.data.x;
          /* console.log('newWidth', newWidth, 'oldWidth', this.#activeInsert.data.width); */
          const updatedData: InsertData = { 
            ...this.#activeInsert.data, 
            width: newWidth,
            unitWidth: newWidth / UNIT_IN_PIXELS
          };
          this.#activeInsert.data = updatedData;
          updateInsert$.next(updatedData);
        }
        break;

      case 'expand-u':
        if (this.#activeInsert) {
          if (this.#activeInsert.type === 'cushion' || this.#activeInsert.type === 'pillow') {
            message$.next({ type: this.#activeInsert.type, show: true });
            return;
          }
          const heightFrom0 = this.#activeInsert.data.y + this.#activeInsert.data.height;
          const closestInsertData = getNearestInsertDataUp(this.#activeInsert.data);
          const newHeight = closestInsertData ? heightFrom0 - (closestInsertData.y + closestInsertData.height) : heightFrom0;
          const updatedData: InsertData = { 
            ...this.#activeInsert.data, 
            height: newHeight,
            unitHeight: newHeight / UNIT_IN_PIXELS,
            y: closestInsertData ? closestInsertData.y + closestInsertData.height : 0
          };
          this.#activeInsert.data = updatedData;
          updateInsert$.next(updatedData);
        }
        break;

      case 'expand-d':
        if (this.#activeInsert) {
          if (this.#activeInsert.type === 'cushion' || this.#activeInsert.type === 'pillow') {
            message$.next({ type: this.#activeInsert.type, show: true });
            return;
          }
          const depth = (depth$.value * UNIT_IN_PIXELS) - Measurement.SEP_WIDTH_PX;
          const closestInsertData = getNearestInsertDataDown(this.#activeInsert.data, depth);
          const newHeight = closestInsertData ? closestInsertData.y - this.#activeInsert.data.y : depth - this.#activeInsert.data.y;
          const updatedData: InsertData = { 
            ...this.#activeInsert.data, 
            height: newHeight,
            unitHeight: newHeight / UNIT_IN_PIXELS
          };
          this.#activeInsert.data = updatedData;
          updateInsert$.next(updatedData);
        }
        break;

    case 'fillgaps':
      let count = 0;
      inserts$.pipe(take(2)).subscribe(inserts => {
        count += 1;
        const updatedData = inserts.map(insert => {
          const { data } = updateInsertDataToFillGaps(
            insert, 
            count == 1 ? { right: true, bottom: true } : { left: true, top: true }, 
            Measurement.UNIT_FRACTION_DISPLAYED_PX * 3
          );
          return data
        });
        const per = getPercentAreaCovered(updatedData);
        /* console.log('percent covered', per); */
        // safer to always update even if there are no changes
        inserts$.next(updatedData);
        //
        if (per < 1) {
          message$.next({ show: true, type: 'empty spaces'});
        } else {
          setTimeout(() => {
            message$.next({ show: false });
          }, 100);
          
        }
        
      })
      break;

      default:
        break;
    }
  }

  #onClick() {
    Logger.info(`${this.#NAME} > #onClick`);
    //
    if (this.#insertsContainer.transformer) this.#insertsContainer.transformer.show = false;
    //
    if (this.#activeInsert) {
      this.#activeInsert.mode = 'nuetral';
      activeInsert$.next(null);
    }
    //
    this.#hideMessage();
  }

  #hideMessage() {
    if (this.#messageBars && this.#messageBars[0].dataset.show === 'true') {
      message$.next({ show: false });
    }
  }

  async #reset() {
    if (inserts$.value.length || document.referrer.includes('/products')) {
      width$.next(Measurement.DEFAULT_WIDTH);
      height$.next(Measurement.MIN_HEIGHT);
      depth$.next(Measurement.DEFAULT_DEPTH);
      inserts$.next([]);
      trayColor$.next(undefined);
    }
    if (!indexDBService.db) await indexDBService.init();
    await indexDBService.clear();
    Logger.info(`${this.#NAME} > #reset`);
  }



}

customElements.define('tray-builder-main', TrayBuilderMain);